// @flow
// Module dependencies
import 'whatwg-fetch';
import CONST from 'app/common/lib/Const';
import Context from 'app/common/lib/context';
import Logging from 'app/common/lib/Logging';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import urlUtil from 'app/common/lib/urlUtil';

const authHandshakeLog = Logging.getLogger('AuthHandshake');

const AuthHandshake = {};

AuthHandshake._parseJson = function(response: Object) {
  return response.json();
};

AuthHandshake._throwError = function(response: Object) {
  const error: Object = new Error(response.statusText);
  error.response = response;
  AuthHandshake._reconstructError(error);
  throw error;
};

AuthHandshake._checkStatus = function(response: Object) {
  if (response.status === 200 || response.status === 401) {
    return response;
  }
  AuthHandshake._throwError(response);
  return {}; // should never happen
};

/**
 * Currently only supports GET and POST requests needed for
 * cross domain authentication.
 */
AuthHandshake._crossDomainRequest = function(
  requestUrl: string,
  paramString: string,
  method: string,
): Object {
  if (!(method === 'GET' || method === 'POST')) {
    return Promise.resolve({});
  }

  const fetchOptions: Object = { credentials: 'include', mode: 'cors' };

  if (method === 'POST') {
    fetchOptions.method = 'POST';
    fetchOptions.body = paramString;
    fetchOptions.headers = {
      'Content-type': 'application/x-www-form-urlencoded; charset=UTF-8',
      'X-Pinterest-InstallId': Context.instance.unauth_id,
    };

    if (!Context.instance.unauth_id) {
      // Add logging to diagnose if missing unauth id is from here
      trackRegisterAction(`unauth.auth_handshake.cross_domain.no_unauth_id.${method}`);
    }
  }

  // TODO: upgrade me!
  return fetch(requestUrl, fetchOptions)
    .then(AuthHandshake._checkStatus)
    .then(AuthHandshake._parseJson)
    .then((response) => {
      if (response.status === 'success') {
        return Promise.resolve(response);
      }
      // reject the Promise when two-factor auth is required
      // all other failures throw an exception as they were before this change
      // note: response.code is only available after deserializing json
      if (response.code === CONST.LOGIN_MFA_REQUIRED) {
        response.api_error_code = response.code;
        return Promise.reject(response);
      }
      AuthHandshake._throwError(response);
      return {}; // should never happen
    })
    .catch((error) => {
      throw error;
    });
};

AuthHandshake._reconstructError = (error: Object) => {
  if (!error.api_error_code && error.response) {
    error.api_error_code = error.response.code;
    error.message = error.response.message;
  }
};

/**
 * Verify if user is logged in on main domain
 * and return custom auth token
 */
AuthHandshake._verifyAndGetToken = function() {
  const requestUrl =
    CONST.GEN.templateConst.settings.ACCOUNTS_PINTEREST_URL + '/v3/handshake/verify/';
  return AuthHandshake._crossDomainRequest(requestUrl, '', 'GET');
};

/**
 * Take in custom auth token, exchange for session token, and set in current browser
 */
AuthHandshake.exchangeTokenAndSetSession = function(
  token: string,
  isRegistration: boolean = false,
) {
  const resource = ResourceFactory.create('HandshakeSessionResource', {
    token,
    isRegistration,
  });
  return resource.callCreate();
};

/**
 * Verify if user is logged in on Pinterest and log
 * them in on the current domain as well if that is the case
 */
AuthHandshake.verifyLoggedInStatus = function() {
  AuthHandshake._verifyAndGetToken().then(
    (response) => {
      if (response && response.data) {
        const token = response.data;
        AuthHandshake.exchangeTokenAndSetSession(token).then(
          (response) => {
            window.location.reload();
          },
          (error) => {
            authHandshakeLog.error('Failed to set session for current user; ' + error.message);
          },
        );
      } else {
        authHandshakeLog.error('No custom access token in cross domain verify response');
      }
    },
    (error) => {
      // handle rejection by doing nothing (swallow error)
      // TODO: add tracking/error handling
    },
  );
};

/**
 * Format user register parameters to be used in Javascript fetch call
 */
AuthHandshake._setUpRegisterParameters = function(options: Object): string {
  const data = {};

  // This should set up the same parameters as seen in user_register_resource.py.

  // TODO(cmiranda): Achieve parity with the regular method of registration if we decide to fully convert
  // to cross domain authentication across all our domains.

  // Unsupported parameters: visited_pages, image_url, personalize_from_offsite_browsing,
  // personalize_nux_from_offsite_browsing, user_to_follow, board_to_follow, pin_to_create,
  // pin_to_create_url, pin_to_create_image_url

  data.email = options.email || '';
  data.username = options.username || '';
  data.password = options.password || '';
  data.first_name = options.first_name || '';
  data.last_name = options.last_name || '';
  data.country = options.country || '';
  data.locale = Context.instance.locale;
  data.referrer = Context.instance.referrer;

  if (options.age) {
    const date = new Date();
    date.setFullYear(date.getFullYear() - options.age);
    const birthday = parseInt(date / 1000, 10);
    data.birthday = birthday.toString();
  }

  if (options.custom_gender) {
    data.custom_gender = options.custom_gender;
  }

  if (options.gender) {
    data.gender = options.gender;
  }

  if (options.business_name) {
    data.account_type = options.account_type;
    data.business_name = options.business_name;
    data.first_name = options.business_name;
  }

  if (Context.instance.invite_code) {
    data.invite_code = Context.instance.invite_code;
  }

  // Add network-specific fields to data
  if (options.facebook_id) {
    const facebookToken = Context.instance.facebook_token || options.facebook_token || '';
    if (!facebookToken) {
      authHandshakeLog.debug('Facebook token is not being set during login call.');
    }

    data.facebook_id = options.facebook_id;
    data.facebook_token = facebookToken;
    data.social_username = options.social_username || '';
  } else if (options.gplus_code || options.gplus_id_token) {
    data.password = options.password || '';
    data.one_time_code = options.gplus_code;
    data.id_token = options.gplus_id_token;
    data.redirect_uri = options.gplus_redirect_uri;
  }
  return urlUtil.getQueryStringFromObject(data);
};

AuthHandshake._setUpLoginParameters = function(creds: Object): string {
  const data = {};

  if (creds.username_or_email) {
    data.username_or_email = creds.username_or_email.trim();
    data.password = creds.password;
    data.referrer = Context.instance.referrer;
  }

  if (creds.facebook_id) {
    const facebookToken = Context.instance.facebook_token || creds.facebook_token || '';
    if (!facebookToken) {
      authHandshakeLog.debug('Facebook token is not being set during login call.');
    }

    data.facebook_id = creds.facebook_id;
    data.facebook_token = facebookToken;
    data.facebook_autologin = creds.facebook_autologin || false;
  }

  if (creds.gplus_id_token) {
    data.gplus_id_token = creds.gplus_id_token;
    data.gplus_access_token = creds.gplus_access_token;
    data.gplus_expires_at = creds.gplus_expires_at;
    data.gplus_autologin = creds.gplus_autologin || false;
  }

  if (creds.mfa_token) {
    data.mfa_token = creds.mfa_token;
  }

  return urlUtil.getQueryStringFromObject(data);
};

AuthHandshake._getRegisterRequestUrl = function(options: Object): string {
  let path = '/v3/register/email/handshake/';

  if (options.facebook_id) {
    path = '/v3/register/facebook/handshake/';
  }

  if (options.gplus_code || options.gplus_id_token) {
    path = '/v3/register/gplus/handshake/';
  }

  if (options.business_name) {
    path = '/v3/register/partner/handshake/';
  }

  return CONST.GEN.templateConst.settings.ACCOUNTS_PINTEREST_URL + path;
};

AuthHandshake._getLoginRequestUrl = function(creds: Object): string {
  let path = '/v3/login/handshake/';

  if (creds.facebook_id) {
    path = '/v3/login/facebook/handshake/';
  }

  if (creds.gplus_id_token) {
    path = '/v3/login/gplus/handshake/';
  }

  if (creds.mfa_token) {
    path = '/v3/login/mfa/handshake/';
  }

  return CONST.GEN.templateConst.settings.ACCOUNTS_PINTEREST_URL + path;
};

/**
 * Register user
 */
AuthHandshake.registerUser = function(options: Object) {
  const requestUrl = AuthHandshake._getRegisterRequestUrl(options);
  const params = AuthHandshake._setUpRegisterParameters(options);
  return AuthHandshake._crossDomainRequest(requestUrl, params, 'POST');
};

/*
 * Login user
 */
AuthHandshake.loginUser = function(creds: Object) {
  const requestUrl = AuthHandshake._getLoginRequestUrl(creds);
  const params = AuthHandshake._setUpLoginParameters(creds);
  return AuthHandshake._crossDomainRequest(requestUrl, params, 'POST');
};

export default AuthHandshake;
